import useCurrentLang from '@/hooks/useCurrentLang';
import useLegacyExternalQuota from '@/hooks/useLegacyExternalQuota';
import LegacyExternalQuotaNotice from '@/components/DmpV2/LegacyExternalQuotaNotice';
import { isExternalReconciliation, isExternalOutcomeUncertain, isLegacyBalanceReconciliation } from '@/utils/legacyExternalQuota';
import { useState, useEffect, useRef } from 'react';
import { isRouteErrorResponse, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Wrapper } from '../styles';
import { Box, Flex, LoadingPage } from '@/layout';
import { Button } from '@/components/UI/Button';
import { InputWrapper, InputFileWrapper, InputFileLabel } from '@/components/Container/AuthContainer/styles';
import { ErrorMessage, Text } from '@/components/UI/Typography/Text';
import { theme } from '@/theme/Theme';
import { CloudUpload } from '@/components/Icon';
import { FormSelect } from '@/components/UI/Select';
import { Modal, TextField } from '@mui/material';
import { casCreateCertApplyOrder, sendCertApplyOrder } from '@/api/eCert.api';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import useResize from '@/hooks/useResize';
import { TCTermsMarkdown } from '@/components/UI/Terms';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { H2 } from '@/components/UI/Typography/Heading';
import { errorHandler } from '@/utils/toastHandler';

interface FormStepperProps {
  setCurrentStep: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  checkLevel: () => boolean;
  previousData?: ECertApplys;
}

const RegistrationForm = ({ previousData, checkLevel, setCurrentStep, setActiveStep }: FormStepperProps) => {
  // @ts-ignore
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { userData } = useAppSelector(getAuthSelector);
  const [searchParmas, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showTCModal, setShowTCModal] = useState(false);
  const [TC, setTC] = useState('');
  const [termsInfo, setTermsInfo] = useState(true);
  const { isMobile } = useResize();
  const location = useLocation();
  const certType = previousData?.cert_type ?? 'organization';
  const english = useCurrentLang() === 'en';
  const quota = useLegacyExternalQuota(certType === 'personal' ? 'hkpost_ecert_personal' : 'hkpost_ecert_organization', userData.user_feature?.ECertApplicantTimes);
  const submitLock = useRef(false);
  const createdDraft = useRef<string>();
  useEffect(() => { if (certType === 'personal') setShowTCModal(false); }, [certType]);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    setError,
    formState: { errors },
  } = useForm<ECertAllplicationRequest>();

  const prevHandler = () => {
    const type = searchParmas.get('type');
    setCurrentStep('2');
    setActiveStep(1);
    setSearchParams({ step: '2', type: type ? type : 'new' });
  };

  const nextHandler = () => {
    const type = searchParmas.get('type');
    setCurrentStep('5');
    setActiveStep(4);
    setSearchParams({ step: '5', type: type ? type : 'new' });
  };

  const onSubmit = handleSubmit(async (data) => {
    if (submitLock.current || certType === 'personal' || !checkLevel()) return;
    let nativeAttempted = false;
    submitLock.current = true; setIsLoading(true); setShowTCModal(false);
    try {
      const access = await quota.refresh();
      if (!['available', 'exhausted'].includes(access.state)) return;
      let applyId = previousData?.id.toString() || createdDraft.current;
      if (!applyId) {
        const res = (await casCreateCertApplyOrder({ ...data, cert_type: 'organization' })).data;
        if (!quota.isCurrent() || !res.success) return;
        applyId = String(res.data.id); createdDraft.current = applyId;
      }
      if (access.state === 'exhausted') {
        navigate(`/cart?item=14&certId=${applyId}`, { state: { from: `${location.pathname}${location.search}` } }); return;
      }
      nativeAttempted = true;
      await sendCertApplyOrder({ applyId }); if (quota.isCurrent()) nextHandler();
    } catch (error) {
      if (isExternalReconciliation(error) || (nativeAttempted && isExternalOutcomeUncertain(error))) quota.markReconciliation(isLegacyBalanceReconciliation(error) ? 'legacy_balance' : undefined); else errorHandler(t('error.server'));
    } finally { submitLock.current = false; setIsLoading(false); }
  });

  useEffect(() => {
    if (!checkLevel()) return;
    if (previousData) {
      setValue('orgEnglishName', previousData.orgEnglishName);
      setValue('orgChineseName', previousData.orgChineseName);
      setValue('contactEnglishName', previousData.contactEnglishName);
      setValue('contactChineseName', previousData.contactChineseName);
      setValue('contactAddress', previousData.contactAddress);
      setValue('emailAddress', previousData.emailAddress);
      setValue('phoneNumber', previousData.phoneNumber);
    }
    fetch(TCTermsMarkdown)
      .then((res) => res.text())
      .then((md) => {
        setTC(md);
      });
  }, [t]);

  const termHandler = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const { currentTarget } = e;

    if (currentTarget.scrollHeight - currentTarget.scrollTop <= currentTarget.clientHeight + 2) {
      setTermsInfo(false);
    }
  };

  return (
    <Wrapper>
      {certType === 'personal' ? <Text role="alert">{english ? 'Personal e-Cert is temporarily unavailable. Existing records are unchanged; organizational allowances will not be used and another payment is not required.' : 'Personal e-Cert 暫不提供，既有紀錄不變；不會改用機構額度或要求再次付款。'}</Text> : <LegacyExternalQuotaNotice decision={quota.decision} retry={quota.refresh} />}
      <LoadingPage isLoading={isLoading} />
      <Modal
        open={showTCModal}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        sx={{ display: 'flex' }}
      >
        <div
          style={{
            margin: 'auto',
            width: '100%',
            maxWidth: '930px',
            backgroundColor: '#fff',
            padding: isMobile ? '15px 25px' : '30px 50px',
            borderRadius: '40px 40px 40px 0px',
          }}
        >
          <H2>{t('pages.auth.signUp.modal.terms.t&c.title')}</H2>
          <span style={{ font: 'Roboto 400 16px', lineHeight: '30px', color: 'red' }}>
            {t('pages.auth.signUp.modal.terms.t&c.title')}
          </span>
          <div
            className="content"
            style={{
              marginTop: '25px',
              height: '40vh',
              overflowY: 'auto',
              fontFamily: 'Roboto',
              fontWeight: '400',
              fontSize: isMobile ? '16px' : '20px',
              color: '#616161',
              lineHeight: '30px',
            }}
            onScroll={termHandler}
          >
            <ReactMarkdown children={TC} remarkPlugins={[remarkGfm]} />
          </div>
          <Flex
            gridGap={isMobile ? 20 : 40}
            marginTop={'28px'}
            alignItems={'center'}
            flexDirection={isMobile ? 'column' : 'row'}
          >
            <Button
              width={isMobile ? '100%' : 'unset'}
              type="button"
              onClick={onSubmit}
              size={'lg'}
              variant={'primary'}
              rounded={false}
              disabled={termsInfo}
            >
              {t('buttons.agree')}
            </Button>
            <Button
              width={isMobile ? '100%' : 'unset'}
              color="primary"
              onClick={() => setShowTCModal(false)}
              style={{ textDecoration: 'underline' }}
            >
              {t('buttons.back')}
            </Button>
          </Flex>
        </div>
      </Modal>
      <Box>
        <InputWrapper style={{ height: 'auto' }}>
          <Controller
            control={control}
            name={'orgEnglishName'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={`${t('form.orgEnglishName.label')} *`}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
              />
            )}
            rules={{ required: t('required.input', { input: t('form.orgEnglishName.label') }) }}
          />
        </InputWrapper>

        <InputWrapper style={{ height: 'auto' }}>
          <Controller
            control={control}
            name={'orgChineseName'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={`${t('form.orgChineseName.label')} *`}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
              />
            )}
            rules={{ required: t('required.input', { input: t('form.orgChineseName.label') }) }}
          />
        </InputWrapper>

        <InputWrapper style={{ height: 'auto' }}>
          <Controller
            control={control}
            name={'contactEnglishName'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={`${t('form.contactEnglishName.label')} *`}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
              />
            )}
            rules={{ required: t('required.input', { input: t('form.contactEnglishName.label') }) }}
          />
        </InputWrapper>

        <InputWrapper style={{ height: 'auto' }}>
          <Controller
            control={control}
            name={'contactChineseName'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={`${t('form.contactChineseName.label')} *`}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
              />
            )}
            rules={{ required: t('required.input', { input: t('form.contactChineseName.label') }) }}
          />
        </InputWrapper>

        <InputWrapper style={{ height: 'auto' }}>
          <Controller
            control={control}
            name={'contactAddress'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={`${t('form.contactAddress.label')} *`}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
              />
            )}
            rules={{ required: t('required.input', { input: t('form.contactAddress.label') }) }}
          />
        </InputWrapper>

        <InputWrapper style={{ height: 'auto' }}>
          <Controller
            control={control}
            name={'emailAddress'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={`${t('form.emailAddress.label')} *`}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
              />
            )}
            rules={{ required: t('required.input', { input: t('form.emailAddress.label') }) }}
          />
        </InputWrapper>

        <InputWrapper style={{ height: 'auto' }}>
          <Controller
            control={control}
            name={'phoneNumber'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={`${t('form.phoneNumber.label')} *`}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
              />
            )}
            rules={{ required: t('required.input', { input: t('form.phoneNumber.label') }) }}
          />
        </InputWrapper>
      </Box>

      <Flex marginTop={50}>
        <Button
          size={'lg'}
          type="button"
          disabled={certType === 'personal'}
          onClick={() => setShowTCModal(true)}
          variant={'primary'}
          rounded={false}
          marginRight={40}
        >
          {t('buttons.next')}
        </Button>
        <Button size={'lg'} type="button" variant="primary-invert" marginRight={40} onClick={prevHandler}>
          {t('buttons.back')}
        </Button>
      </Flex>
    </Wrapper>
  );
};

export default RegistrationForm;
